import React from "react";
import {
  SEO,
  PageLayout,
  Section,
  FadeReveal,
  FishermanHeader,
  MarkdownContent,
  WebsiteComponentInteractions,
  OutboundLink,
  Location,
  HoursOfOperation,
  BasicCarousel,
  componentIterator,
  Image,
  ContactForm,
  Content,
} from "@bluefin/components";
import { Grid, Button } from "semantic-ui-react";
import { graphql } from "gatsby";

import WebsiteLayout from "../components/WebsiteLayout.jsx";
import {
  getComponentFiles,
  getComponentContentNodeContent,
  getComponentInteractions,
} from "../utils/utils";

export default class ContactPage extends React.PureComponent {
  render() {
    const { data } = this.props;
    const { fishermanBusiness, fishermanBusinessWebsitePage, bookingUrl } =
      data;

    return (
      <WebsiteLayout>
        <SEO
          businessName={fishermanBusiness.name}
          pageName={fishermanBusinessWebsitePage.title}
          description={fishermanBusinessWebsitePage.description}
          images={getComponentFiles({
            components: fishermanBusinessWebsitePage.components,
            componentName: "HeroElement",
            numToSelect: 1,
          })}
          title={fishermanBusinessWebsitePage.seoTitle}
          ogUrl={"https://www.elanhairstudionj.com/contact/"}
        />
        <PageLayout className={"contact-page"} hero={null}>
          <Section className={"default-hero-section"}>
            <Grid stackable={true}>
              <Grid.Column className={"information"}>
                <FadeReveal duration={1250} triggerOnce={true} delay={0}>
                  <div>
                    <FishermanHeader
                      as={"h1"}
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "contact_hero_header",
                        defaultValue: "Contact Us",
                      })}
                    />
                    <MarkdownContent
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "contact_hero_description",
                      })}
                    />
                    <WebsiteComponentInteractions
                      alignment={"center"}
                      ctas={getComponentInteractions({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "contact_hero_description",
                      })}
                      fallback={
                        <React.Fragment>
                          <Button
                            primary={true}
                            as={OutboundLink}
                            to={bookingUrl.url}
                            event={{
                              category: "Booking",
                              action: "Schedule Appointment Intent",
                            }}
                            content={bookingUrl.title}
                          />
                        </React.Fragment>
                      }
                    />
                    <div className={"contact-info"}>
                      <Location
                        phone={fishermanBusiness.primaryLocation.phoneNumber}
                        address={{
                          street: fishermanBusiness.primaryLocation.street,
                          city: fishermanBusiness.primaryLocation.city,
                          state: fishermanBusiness.primaryLocation.state,
                          zip: fishermanBusiness.primaryLocation.zipCode,
                        }}
                        businessName={fishermanBusiness.name}
                        showBusinessName={false}
                      />
                      <HoursOfOperation
                        hours={fishermanBusiness.primaryLocation.hours}
                        displayOption={"grouped"}
                        inlineHours={true}
                        truncateLabels={true}
                      />
                    </div>
                  </div>
                </FadeReveal>
              </Grid.Column>
              <Grid.Column className={"images"}>
                <BasicCarousel
                  size={1}
                  slide={true}
                  displayIndicators={false}
                  displayPlayPauseButton={false}
                  autoScroll={false}
                  leftArrowIcon={"arrow-left"}
                  rightArrowIcon={"arrow-right"}
                  mobile={{ size: 1 }}
                  items={componentIterator({
                    iterator: getComponentFiles({
                      components: fishermanBusinessWebsitePage.components,
                      componentIdentifier: "hero_images",
                    }),
                    component: <Image background={true} />,
                    propMap: { src: "__all__" },
                  })}
                />
              </Grid.Column>
            </Grid>
          </Section>
          <Section className={"contact-form-section"}>
            <ContactForm
              buttonText={"Send Message"}
              centeredHeaders={true}
              hideOptIn={false}
              className={""}
              fluidButton={false}
              header={
                <div className={"contact-form-header"}>
                  <Content columns={1}>
                    <Content.Markup
                      isMarkdown={false}
                      textAlign={"center"}
                      as={"h3"}
                      className={"ui header"}
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "contact_form_section_header",
                        defaultValue: "Get In Touch",
                      })}
                    />
                    <Content.Markup
                      isMarkdown={true}
                      textAlign={"center"}
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "contact_form_section_description",
                      })}
                    />
                    <WebsiteComponentInteractions
                      alignment={"center"}
                      ctas={getComponentInteractions({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "contact_form_section_description",
                      })}
                    />
                  </Content>
                </div>
              }
              inline={false}
              showLabels={true}
              subheader={""}
              termsCopy={""}
              withContainer={false}
              locationId={fishermanBusiness.primaryLocation._id}
              businessId={fishermanBusiness._id}
              businessType={fishermanBusiness.type}
              event={{ category: "Contact", action: "Submit Inquiry" }}
            />
          </Section>
          <Section className={"book-now-section"}>
            <Image
              background={true}
              src={getComponentFiles({
                components: fishermanBusinessWebsitePage.components,
                componentIdentifier: "book_now_section_background_image",
                numToSelect: 1,
              })}
            >
              <FishermanHeader
                as={"h2"}
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_header",
                  defaultValue: "Ready to book?",
                })}
              />
              <MarkdownContent
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_description",
                  defaultValue: "",
                })}
              />
              <WebsiteComponentInteractions
                alignment={"center"}
                ctas={getComponentInteractions({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "book_now_section_description",
                })}
                fallback={
                  <React.Fragment>
                    <Button
                      primary={true}
                      as={OutboundLink}
                      to={bookingUrl.url}
                      event={{
                        category: "Booking",
                        action: "Schedule Appointment Intent",
                      }}
                      content={bookingUrl.title}
                    />
                  </React.Fragment>
                }
              />
            </Image>
          </Section>
          <Section className={"about-section"}>
            <Grid stackable={true}>
              <Grid.Column width={8}>
                <FadeReveal duration={1250} triggerOnce={true} delay={0}>
                  <div>
                    <FishermanHeader
                      as={"h2"}
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_header",
                        defaultValue: "Where We Shine",
                      })}
                    />
                    <MarkdownContent
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_description",
                      })}
                    />
                    <WebsiteComponentInteractions
                      ctas={getComponentInteractions({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_description",
                      })}
                      fallback={
                        <React.Fragment>
                          <Button
                            primary={true}
                            as={OutboundLink}
                            to={bookingUrl.url}
                            event={{
                              category: "Booking",
                              action: "Schedule Appointment Intent",
                            }}
                            content={bookingUrl.title}
                          />
                        </React.Fragment>
                      }
                    />
                  </div>
                </FadeReveal>
              </Grid.Column>
              <Grid.Column width={8}>
                <FadeReveal duration={1250} triggerOnce={true} delay={450}>
                  <div>
                    <FishermanHeader
                      as={"h2"}
                      className={"highlighted"}
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "about_section_highlight_header",
                      })}
                    />
                  </div>
                </FadeReveal>
              </Grid.Column>
            </Grid>
          </Section>
        </PageLayout>
      </WebsiteLayout>
    );
  }
}

export const query = graphql`
  query {
    fishermanBusiness {
      name
      primaryLocation {
        phoneNumber
        street
        city
        state
        zipCode
        hours {
          day
          open
          close
        }
        _id
      }
      _id
      type
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Contact" }) {
      title
      description
      components {
        fastId
        componentIdentifier
        order
        files {
          file
          altText
          gatsbyFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        contentNodes {
          content
        }
        interactions {
          _id
          order
          behaviorType
          displayType
          enabled
          label
          style
          url
          role
        }
      }
      seoTitle
    }
    bookingUrl: fishermanBusinessWebsitePage(
      title: { regex: "/Book Now|Make An Appointment/" }
      pageType: { eq: "External" }
    ) {
      url
      title
    }
  }
`;
